import { displaySection } from "./mainfeedpage.js"
import { updateToPost } from "../helpers/collapse.js"
import { UserPageFollowManager } from "../ui/userfollow.js"
import { escapeHtml } from "../../utils/common.js"
import { getRecordsHistoryIndex, popoverInit, PATH } from "../helpers/shared-record-path.js"
import { createMenuButton, LongModal, EditModal, SubmitModal, createRecordDeletionModalElement, createReportUserButton } from "../components/build-modal.js"
import { initButtons, resetInnerHtmlForAll } from "../helpers/collapse.js"
import { isBlocked, isBlockedUserPage } from "../helpers/report.js"
import { showPageNotFound } from "../../utils/common.js"
import { createUserShareModal } from "../components/build-share-modal.js"
import { getMembershipTitle } from "../../purchase/purchase.js"
import { getTimeAgo } from "../helpers/time-converter.js"
import { loadProficiencyTrendChart, proficiencyTrendChartId } from "../components/chart.js"
import { RecordItemWithoutImage, RecordItemWithImage, getFirstValidId } from "../components/record-item.js"
import { el, mount } from "redom"
import { SectionTitle } from '../components/section-title.js'
import { isAndroidApp } from '../../utils/app-type-checker.js'
import { getAuthUserWithHandlingError as getAuthUser, maybeMeIsMe } from '../../utils/authUser.js'
import { logoutOnServer, reloadThePage } from "../../login/login.js"
import { fetchGetJson, fetchPostJsonIfValid, handleErrorWithAlert } from "../../utils/fetch-json.js"
import { createFormData } from "../../utils/formdata.js"
import app from "../../utils/app.js"
import { Modal } from "bootstrap"
import { hostAppCall } from "../../utils/appFunctions.js"
import type { MaybeUser, UserPage } from "#/types"
import type { AppWebInterface } from "#/app.js"
app.saveAppInfoInCookie()
if (!window.WEBVIEW01) window.WEBVIEW01 = {}

export const navigateToUserPage = () => new UserPageController(PATH.getUserId()).init(false)
export const openUserPageOnClick = (userId: string) => new UserPageController(userId).init(true)
export const openOrReturnToUserPage = (userId: string) => {
    const userPath = "/u/" + userId
    if (getRecordsHistoryIndex() > 0 && window.history.state?.previousRecordsPath == userPath)
        window.history.back()
    else
        new UserPageController(userId).init(true, false)
}

export class UserPageController {
    private recordsPage: HTMLElement;
    private defaultSharedRecordsNumber = 6
    private defaultLikedRecordsNumber = 4
    constructor(private userId: string) {
        this.recordsPage = document.getElementById("records-page")!;
    }
    private highlightNumber = (userInfo: UserPage) => Math.min(Math.floor(userInfo.sharedRecords.length / 10 + 1), 4)
    async init(clicked: boolean, addToHistory: boolean = true) {
        window.WEBVIEW01.sharedRecords = {
            onComplete: {
                getSubscribedProductId: this.renderManageSubscriptionButton.bind(this)
            }
        } satisfies AppWebInterface["sharedRecords"]

        if (addToHistory) PATH.addHistory("/u/" + this.userId, clicked)
        else PATH.replaceHistory("/u/" + this.userId)
        const maybeMe = await getAuthUser();
        if (isBlockedUserPage(this.userId)) return
        if (clicked) window.scrollTo(0, 0)

        resetInnerHtmlForAll()
        initButtons()

        const userInfo = await fetchGetJson<UserPage>("/v1/u/" + this.userId).catch(() => { showPageNotFound(); return null; })
        if (!userInfo) return showPageNotFound()
        userInfo.likedRecords = userInfo.likedRecords.filter(r => !isBlocked(r.uploader.id))

        document.title = userInfo.uploader.name + "'s page - Soundcorset"

        await this.initUserInformation(userInfo, maybeMe)
        this.initRecords(userInfo)

        if (!this.recordsPage) return
        mount(this.recordsPage, this.initProficiencyGraph(userInfo))

        this.initLikedRecords(userInfo)
        if (userInfo.proficiencyChartData.labels.length > 0) {
            const chart = loadProficiencyTrendChart()
            chart.data.labels = userInfo.proficiencyChartData.labels
            chart.data.datasets[0].data = userInfo.proficiencyChartData.data
            chart.update()
        }
        const modals = document.createDocumentFragment()
        const followersModal = new LongModal("followersModal", "FOLLOWERS", el("p#modalFollowerContext.noSpace", "Loading followers"))
        const followingsModal = new LongModal("followingsModal", "FOLLOWINGS", el("p#modalFollowingContext.noSpace", "Loading followings"))
        const recordDeletionModal = createRecordDeletionModalElement()
        const userShareModal = createUserShareModal(userInfo.uploader.id)
        modals.appendChild(followersModal.el)
        modals.appendChild(followingsModal.el)
        modals.appendChild(recordDeletionModal)
        modals.appendChild(userShareModal.el)
        modals.appendChild(this.createSettingsModal())
        mount(this.recordsPage, modals)
        popoverInit()
    }

    private showManageSubscriptionButton(userInfo: UserPage) {
        if (isAndroidApp())
            hostAppCall("getSubscribedProductId")
        else if (userInfo.maybeMembership.isDefined)
            this.renderManageSubscriptionButton(userInfo.maybeMembership.get)
    }

    private renderManageSubscriptionButton(productId: string | null) {
        const membershipHTML = document.getElementById("sc_membership_div")
        const membershipTitle = getMembershipTitle(productId)
        if (!membershipHTML) return
        mount(membershipHTML,
            el("p.sc_membership_your_plan", { style: "padding-top: 20px; text-align: center;", textContent: "Your plan is " },
                el("span#plan_name", { style: "font-weight: 500", textContent: membershipTitle }))
        )
        mount(membershipHTML,
            el("#manageMembership.sc_membership_div text-center", { style: "padding: 10px 20px 20px 20px" },
                el("button.btn btn btn-lg sc_membership_button", { type: "button", textContent: "MANAGE MEMBERSHIP", onclick: () => window.location.href = "/public/purchase.html?backToPrev" })
            )
        )
    }

    private async initUserInformation(userInfo: UserPage, maybeMe: MaybeUser) {
        const userInfoHTML = el(".userInformation")
        const isMyPage = maybeMeIsMe(maybeMe) && maybeMe.get.id == userInfo.uploader.id
        const isAdmin = maybeMeIsMe(maybeMe) && maybeMe.get.isAdmin
        if (isMyPage || isAdmin) {
            const buttonsDiv = el(".buttons", { style: "display: flex; position: absolute; right: -8px; flex-direction: column; align-items: end;" })
            if (isMyPage) {
                mount(buttonsDiv, el("button#logout.btn btn-outline-dark.btn-sm", { onclick: () => logoutOnServer(), textContent: "Sign out" }))
                mount(buttonsDiv, el("button#settings.btn btn-outline-dark.btn-sm", { onclick: () => Modal.getOrCreateInstance("#settingsModal").show(), textContent: "Settings" }))
            }

            if (isAdmin)
                mount(buttonsDiv, el("button.#checkBuildInfo.btn btn-outline-dark", { align: "center", onclick: () => { window.location.href = "/dynamic/build" }, textContent: "Build Info" }))
            mount(userInfoHTML, el("", { style: "position: relative;" }, buttonsDiv))
        }
        mount(userInfoHTML,
            el("", { align: "center", style: "padding-top: 10px;" },
                el("", { style: "padding:10px" },
                    el("img.rounded-circle", { src: userInfo.uploader.imgUrl, width: "128", onerror: (e: Event) => { (e.target as HTMLImageElement).src = "/public/img/icon200x200.png" } })
                ),
                el(".noPadding",
                    el("h5", { style: "font-weight:500; letter-spacing: 1px; margin: 0px;", textContent: userInfo.uploader.name })
                )
            ))
        mount(userInfoHTML, el("#sc_membership_div"))

        if (isMyPage) {
            mount(userInfoHTML, el("h3.pb-3 mb-3 border-bottom"))
            mount(userInfoHTML, this.createBiographyEditModal(userInfo))
        } else {
            if (userInfo.uploader.biography.isDefined) {
                mount(userInfoHTML, el("h3.pb-3 mb-3 border-bottom"))
                mount(userInfoHTML, el(".biography", el("text#biography-text", { textContent: userInfo.uploader.biography.get })))
            }
        }
        mount(userInfoHTML, el("h3.pb-3 mb-3 border-bottom"))
        const followManager = new UserPageFollowManager(userInfo, maybeMe.isDefined, isMyPage)
        mount(userInfoHTML, followManager)
        mount(followManager,
            el("", { align: "center" },
                el("button.modalBtn clickable", { type: "button", "data-bs-toggle": "modal", 'data-bs-target': "#shareModal" },
                    el("i.fas fa-share-alt")
                )
            )
        )

        if (!isMyPage) {
            const buttons = [createReportUserButton(userInfo.uploader, maybeMeIsMe(maybeMe) ? maybeMe.get.id : undefined)] // can't use optional chaining in iOS 12.5.5
            mount(followManager, el("div",{align: "center" }, createMenuButton("more_contents", buttons, userInfoHTML)))
        }

        if (this.recordsPage) mount(this.recordsPage, userInfoHTML)
        if (isMyPage && isAndroidApp()) this.showManageSubscriptionButton(userInfo)
    }


    private initRecords = (userInfo: UserPage) => {
        const defaultHighlightRecordsNumber = this.highlightNumber(userInfo)
        if (userInfo.sharedRecords.length > 5 && userInfo.highlightRecords.length > 0) {
            const highlightRecords = userInfo.highlightRecords.map(e => { return { file: e, uploader: userInfo.uploader } })
            displaySection("HIGHLIGHT", RecordItemWithoutImage, highlightRecords, defaultHighlightRecordsNumber, "highlights", "high" + userInfo.uploader.id)
        }
        // NOTE: The reason using "if (userInfo.sharedRecords.length > 0)" instead of checking "getFirstValidId" is there could be only PREPROCESSING files in shared records list
        if (userInfo.sharedRecords.length > 0) {
            const sharedRecords = userInfo.sharedRecords.map(e => { return { file: e, uploader: userInfo.uploader } })
            displaySection("SHARED RECORDS", RecordItemWithoutImage, sharedRecords, this.defaultSharedRecordsNumber, "shared", "user" + userInfo.uploader.id)
        }
    }

    private initLikedRecords = (userInfo: UserPage) => {
        if (getFirstValidId(userInfo.likedRecords))
            displaySection("FAVORITES", RecordItemWithImage, userInfo.likedRecords, this.defaultLikedRecordsNumber, "liked", "like" + userInfo.uploader.id)
    }

    private initProficiencyGraph(userInfo: UserPage) {
        const graphHTML = el("")

        if (userInfo.proficiencyChartData.labels.length > 0 || userInfo.unevaluatedRecords.length > 0) {
            mount(graphHTML, el(".row sc-section-divider"))
            const scSec = el(".sc-section")
            const section = new SectionTitle()
            section.updateTitle("PROFICIENCY TREND", true)
            section.border.style.paddingBottom = "0.25rem"
            section.border.style.marginBottom = "0.25rem"
            mount(section.row, el(".col-auto", el("button.modalBtn", { type: "button", style: "font-size: 16px", tabindex: '0', "data-bs-toggle": "popover", 'data-bs-content': 'This graph shows the relative proficiency trend of the musical instrument being played. The recording should be long enough in order to be evaluated by our artificial intelligence system.', "data-bs-placement": "right" }, el("i.fas fa-info-circle clickable", { style: "margin: 12px 0 0 5px; color: #666666" }))))
            mount(scSec, section)
            mount(graphHTML, scSec)
            if (userInfo.proficiencyChartData.labels.length > 0) {
                const chartContainer = el(".chart-container", el("canvas", { id: proficiencyTrendChartId, role: "img" }))
                mount(section, chartContainer)
            }
            if (userInfo.unevaluatedRecords.length > 0) {
                const paddedDiv = el("", { style: "padding: 15px 0 0 0px;" })
                mount(section, paddedDiv)
                userInfo.unevaluatedRecords.forEach(record =>
                    mount(paddedDiv, el(".sc_user_graph_list clickable", { onclick: () => updateToPost(record.id) },
                        el("span.sc_user_graph_list_title", `· ${escapeHtml(record.title)}`), " will be evaluated ", el("span", getTimeAgo(record.evaluationAvailableAt))))
                )
            }
        }
        return graphHTML
    }

    private createSettingsModal() {
        type SettingsItem = { icon: string; label: string; onclick: () => void; subModal?: HTMLElement }

        const signOutAllModal = new SubmitModal(
            "signOutAllDevicesModal",
            "SIGN OUT FROM ALL OTHER DEVICES",
            el("p", { style: "padding: 1rem; margin: 0; color: var(--text-lighter);", textContent: "This will sign you out from all other active sessions. Your current session will remain active." }),
            "SIGN OUT",
            () => { fetchPostJsonIfValid("/v1/me/logoutOthers").then(() => reloadThePage()).catch(handleErrorWithAlert) }
        )

        const items: SettingsItem[] = [
            {
                icon: "fas fa-sign-out-alt fa-fw",
                label: "Sign out from all other devices",
                onclick: () => Modal.getOrCreateInstance("#signOutAllDevicesModal").show(),
                subModal: signOutAllModal.el
            }
        ]

        const settingsBody = el("div")
        items.forEach(({ icon, label, onclick }) =>
            mount(settingsBody, 
                el("div.modalBtn.clickable.menu", { style: "display: block; padding: 0 1rem;", onclick },
                    el("span.clickable.content", { style: "margin: 5px 0px;" }, el(`i.${icon}`)),
                    el("span.clickable.content", { style: "margin: 5px 10px;" }, label)
                )
            )
        )

        const settingsModal = new LongModal("settingsModal", "SETTINGS", settingsBody)

        const fragment = document.createDocumentFragment()
        fragment.appendChild(settingsModal.el)
        items.forEach(({ subModal }) => { if (subModal) fragment.appendChild(subModal) })
        return fragment
    }

    private createBiographyEditModal(userInfo: UserPage) {
        const textForEmptyBiography = "Describe yourself here!"
        const biographyText = el("text#biography-text", { style: "text-decoration:underline", textContent: userInfo.uploader.biography.get })
        const biographyEmpty = el("text#biography-empty", { style: "text-decoration: underline; display: " + (userInfo.uploader.biography.isDefined ? "none" : "inline-block") }, textForEmptyBiography)
        const biographyEditBox = el("textarea#biotext", { "rows": 5, "name": "biography", "maxlength": "500", style: "width: 100%; overflow: auto; resize: none;", placeholder: textForEmptyBiography })
        const displayBiography = (biography = "") => {
            biographyText.textContent = biography
            biographyEmpty.style.display = biography == "" ? "inline-block" : "none"
        }
        const editBiography = () => {
            const biography = biographyEditBox.value
            const fd = createFormData({ biography })
            const url = "/v1/me/biography/edit"
            return fetchPostJsonIfValid(url, fd).then(() => displayBiography(biography)).catch(handleErrorWithAlert)
        }
        const editBiographyModal = new EditModal("biography_modal", "EDIT BIOGRAPHY", biographyEditBox, "SAVE", editBiography)
        return el("div",
            el(".biography clickable", { 'data-bs-toggle': "modal", 'data-bs-target': "#biography_modal", onclick: () => {
                    Modal.getOrCreateInstance("#biography_modal").show();
                    biographyEditBox.value = biographyText.textContent ?? "" } },
                biographyText,
                biographyEmpty,
                el("i.fas fa-edit", { style: "font-size: 15px; padding-left: 5px" })),
            editBiographyModal.el)
    }
}
